/* eslint-disable */
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { StepHelpers } from "@shared/components/Stepper";
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  CheckCircle2,
  ShieldCheck,
  Zap,
  Check,
  Pencil,
} from "lucide-react";
import { toast } from "@shared/hooks/use-toast";
import { PageHeader } from "@shared/components/PageHeader";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { ActivateDialog } from "./activateDialogue";
import { useGetChoices } from "@features/ix/hooks";

interface ReviewStepProps {
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  totalSteps?: number;
  onNext?: () => void;
  onPrevious?: () => void;
  onGoToStep?: (stepIndex: number) => void;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
}

export const ReviewStep: React.FC<ReviewStepProps> = ({
  stepHelpers,
  currentStepIndex = 1,
  totalSteps = 2,
  onPrevious,
  onGoToStep,
  setValidationError,
  clearValidationError,
}) => {
  const navigate = useNavigate();
  const { data: directionData } = useGetChoices("direction");

  const directionChoices = useMemo(
    () =>
      directionData?.map((d: any) => ({
        value: d.key,
        label: d.value,
      })) || [],
    [directionData],
  );

  const [isActivating, setIsActivating] = useState(false);
  const [isAgreed, setIsAgreed] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // States to hold compiled config
  const [connectionData, setConnectionData] = useState<any>({});

  const dataLoadedRef = useRef(false);

  // Load saved data from all previous steps
  useEffect(() => {
    if (stepHelpers && !dataLoadedRef.current) {
      try {
        const step0 = stepHelpers.getStepData(0) || {}; // Connection

        setConnectionData(step0);

        dataLoadedRef.current = true;
      } catch (error) {
        console.error("Failed to load review step data:", error);
        dataLoadedRef.current = true;
      }
    }
  }, [stepHelpers]);

  const handleActivate = async () => {
    setIsActivating(true);
    if (clearValidationError) {
      clearValidationError();
    }

    try {
      // Save activation state in localStorage so the landing page reflects connection status
      localStorage.setItem("Paymentus_connected", "true");

      // Save connection configuration to simulate persistent settings
      const finalConfig = {
        connection: connectionData,
        activatedAt: new Date().toISOString(),
      };
      localStorage.setItem("Paymentus_config", JSON.stringify(finalConfig));

      // Reset the stepper data
      if (stepHelpers) {
        stepHelpers.resetAllData();
      }

      toast({
        title: "Integration Activated",
        description:
          "Paymentus Paymentus integration has been successfully activated.",
      });

      // Redirect back to Paymentus Landing page
      setTimeout(() => {
        setIsDialogOpen(false);
        navigate("/integration-hub/Paymentus-integration");
      }, 1000);
    } catch (error) {
      console.error("Failed to activate integration:", error);
      if (setValidationError) {
        setValidationError("Failed to activate integration. Please try again.");
      }
      setIsActivating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Step Header */}
      <div className="flex items-center justify-between border-b pb-4">
        <PageHeader
          title="Review & Activate"
          subtitle="Final summary. Once activated, syncing begins immediately."
        />
        <Badge className="bg-blue-50 text-blue-700 hover:bg-blue-50 border border-blue-200 text-[12px] font-medium py-1 px-3 rounded-full shrink-0">
          Pre-flight checks: passed
        </Badge>
      </div>

      {/* Overview Success Banner */}
      <div className="bg-emerald-50/50 border border-emerald-100 rounded-2xl p-4 flex gap-3 items-start">
        <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 shrink-0 mt-0.5">
          <Check className="w-3 h-3" strokeWidth={3} />
        </div>
        <div className="space-y-0.5">
          <h3 className="text-sm font-bold text-emerald-800 leading-normal">
            All checks passed — ready to activate
          </h3>
          <p className="text-xs text-emerald-650 font-semibold leading-relaxed">
            Connection verified
          </p>
        </div>
      </div>

      {/* Grid of Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Card 1: Endpoints */}
        <div className="p-6 border border-gray-200 rounded-2xl bg-white flex flex-col justify-between space-y-4">
          <div className="flex items-center justify-between border-b border-gray-100 pb-3">
            <h2 className="text-base font-bold text-gray-900">Endpoints</h2>
            <Button
              variant="ghost"
              onClick={() => onGoToStep?.(0)}
              className="h-8 px-2 text-[#5850ec] hover:text-[#4f46e5] font-semibold text-xs gap-1.5 flex items-center hover:bg-transparent"
            >
              <Pencil className="w-3.5 h-3.5" />
              <span>Edit</span>
            </Button>
          </div>
          <div className="flex-1 space-y-4 pt-2">
            <div className="space-y-1">
              <span className="text-xs font-semibold text-gray-500">Account Lookup</span>
              <div className="flex items-center gap-2">
                <Badge className="bg-gray-100 text-gray-600 hover:bg-gray-100 font-mono text-[10px] px-1.5 py-0.5 rounded shadow-sm border border-gray-200">GET</Badge>
                <span className="font-mono text-[11px] text-gray-700 break-all">{connectionData.accountLookupUrl || "—"}</span>
              </div>
            </div>
            
            <div className="space-y-1">
              <span className="text-xs font-semibold text-gray-500">Payment Notification</span>
              <div className="flex items-center gap-2">
                <Badge className="bg-gray-100 text-gray-600 hover:bg-gray-100 font-mono text-[10px] px-1.5 py-0.5 rounded shadow-sm border border-gray-200">POST</Badge>
                <span className="font-mono text-[11px] text-gray-700 break-all">{connectionData.paymentNotificationUrl || "—"}</span>
              </div>
            </div>

            <div className="flex items-center justify-between pt-2">
              <span className="text-sm font-semibold text-gray-600">Connection</span>
              <span className="inline-flex items-center gap-1.5 text-xs font-bold px-2 py-0.5 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                Verified
              </span>
            </div>
          </div>
        </div>

        {/* Card 2: Auth & Settings */}
        <div className="p-6 border border-gray-200 rounded-2xl bg-white flex flex-col justify-between space-y-4">
          <div className="flex items-center justify-between border-b border-gray-100 pb-3">
            <h2 className="text-base font-bold text-gray-900">Auth & Settings</h2>
            <Button
              variant="ghost"
              onClick={() => onGoToStep?.(0)}
              className="h-8 px-2 text-[#5850ec] hover:text-[#4f46e5] font-semibold text-xs gap-1.5 flex items-center hover:bg-transparent"
            >
              <Pencil className="w-3.5 h-3.5" />
              <span>Edit</span>
            </Button>
          </div>
          <div className="flex-1 space-y-3 pt-2">
            <ValueLabel
              label="Username"
              value={
                <span className="font-mono text-[11px] text-gray-700 text-right">
                  {connectionData.username || "— (not set)"}
                </span>
              }
              layout="horizontal"
              className="justify-between w-full py-1 border-gray-100/50"
            />
            <ValueLabel
              label="Password"
              value={
                <span className="font-mono text-[11px] font-bold tracking-[0.2em] text-gray-900 text-right pt-1">
                  {connectionData.password ? "••••••••" : "—"}
                </span>
              }
              layout="horizontal"
              className="justify-between w-full py-1 border-gray-100/50"
            />
            <ValueLabel
              label="Enterprise ID"
              value={
                <span className="font-mono text-[11px] text-gray-700 text-right">
                  {connectionData.enterpriseId || "— (not set)"}
                </span>
              }
              layout="horizontal"
              className="justify-between w-full py-1 border-gray-100/50"
            />
            <ValueLabel
              label="Company ID"
              value={
                <span className="font-mono text-[11px] text-gray-700 text-right">
                  {connectionData.companyId || "— (not set)"}
                </span>
              }
              layout="horizontal"
              className="justify-between w-full py-1 border-gray-100/50"
            />
            
            <div className="flex items-center justify-between pt-2 mt-2 border-t border-gray-100/50">
              <span className="text-sm font-semibold text-gray-600">Duplicate 409</span>
              <span className="inline-flex items-center text-[10px] font-bold px-2 py-0.5 rounded border bg-emerald-50 text-emerald-700 border-emerald-200 uppercase tracking-wide">
                Named alert
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Checkbox */}
      <div className="flex items-center gap-3 py-4 border-t border-gray-100 mt-6">
        <input
          id="agree-checkbox"
          type="checkbox"
          checked={isAgreed}
          onChange={(e) => setIsAgreed(e.target.checked)}
          className="h-4.5 w-4.5 rounded border-gray-300 text-[#5850ec] focus:ring-[#5850ec] cursor-pointer"
        />
        <label
          htmlFor="agree-checkbox"
          className="text-xs sm:text-sm font-semibold text-gray-700 cursor-pointer select-none leading-none"
        >
          I understand activating will start syncing live data with Paymentus
          production.
        </label>
      </div>

      {/* Navigation Footer */}
      <div className="flex items-center justify-between pt-6 border-t mt-4">
        <Button
          onClick={onPrevious}
          variant="outline"
          disabled={isActivating}
          className="h-10 px-5 rounded-xl border-gray-300 hover:bg-gray-50 text-gray-700 font-semibold gap-2 flex items-center"
        >
          <ChevronLeft className="w-4 h-4" />
          <span>Back</span>
        </Button>
        <div className="flex items-center gap-4">
          <Button
            onClick={() => setIsDialogOpen(true)}
            disabled={isActivating || !isAgreed}
            variant="default"
            className="h-10 px-5 rounded-xl font-bold bg-[#5850ec] hover:bg-[#4f46e5] text-white gap-2 flex items-center shadow-sm disabled:opacity-50"
          >
            {isActivating ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Activating...</span>
              </>
            ) : (
              <>
                <Zap className="w-4 h-4 text-white fill-white" />
                <span>Activate Integration</span>
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Confirmation Activation Dialogue */}
      <ActivateDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onConfirm={handleActivate}
        isActivating={isActivating}
        syncFlowsCount={0}
      />
    </div>
  );
};

export default ReviewStep;
